import { useRef, useState } from "react";
import { mediaUrl, pieceName } from "../media";
import Photo from "./Photo";

const TAP_SLOP = 10;

function CloseIcon() {
    return (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

export default function PickerSheet({ mode, title, note, images, selectedIds, loading, max, onClose, onConfirm, onUpload }) {
    const gesture = useRef({ x: 0, y: 0, moved: false });
    const swallowClick = useRef(null);
    const [draft, setDraft] = useState(selectedIds || []);

    function clearSwallow() {
        if (!swallowClick.current) return;
        window.removeEventListener("click", swallowClick.current, true);
        swallowClick.current = null;
    }

    function onTouchStart(event) {
        const point = event.touches[0];
        if (!point) return;
        clearSwallow();
        gesture.current = { x: point.clientX, y: point.clientY, moved: false };
    }

    function onTouchMove(event) {
        const point = event.touches[0];
        const start = gesture.current;
        if (!point || start.moved) return;
        const dx = point.clientX - start.x;
        const dy = point.clientY - start.y;
        if (dx * dx + dy * dy > TAP_SLOP * TAP_SLOP) start.moved = true;
    }

    function onTouchEnd() {
        const active = gesture.current;
        if (!active.moved) return;
        const swallow = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (gesture.current === active) active.moved = false;
            clearSwallow();
        };
        clearSwallow();
        swallowClick.current = swallow;
        window.addEventListener("click", swallow, true);
        window.setTimeout(() => {
            if (swallowClick.current !== swallow) return;
            clearSwallow();
            if (gesture.current === active) active.moved = false;
        }, 700);
    }

    function choose(image) {
        if (gesture.current.moved) return;
        if (mode === "single") {
            onConfirm([image]);
            return;
        }
        setDraft((current) => {
            if (current.some((item) => item.id === image.id)) return current.filter((item) => item.id !== image.id);
            if (max && current.length >= max) return current;
            return current.concat(image);
        });
    }

    const many = mode === "many";

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-overlay md:items-center md:p-8" role="dialog" aria-modal="true" aria-label={title}>
            <div className="flex h-full w-full flex-col gap-3 bg-white p-4 md:h-[min(720px,100%)] md:max-w-2xl md:rounded-2xl md:shadow-[0_0_0_1px_var(--hairline)]">
                <div className="flex items-center gap-3">
                    <h2 className="flex-1 font-Mulish text-lg font-semibold text-secondary">{title}</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">
                        <CloseIcon />
                    </button>
                </div>
                {note ? <p className="font-Mulish text-sm text-graphite">{note}</p> : null}
                {many && max && draft.length >= max ? (
                    <p className="font-Mulish text-sm text-graphite">A look can include {max} pieces.</p>
                ) : null}
                <div
                    className="min-h-0 flex-1 overflow-y-auto rounded-2xl bg-primary-50 p-2 touch-pan-y"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {loading ? (
                        <div className="grid grid-cols-2 content-start gap-3 sm:grid-cols-3" role="status" aria-label="Loading photos">
                            {Array.from({ length: 6 }, (_, index) => (
                                <span key={index} className="vton-wave aspect-[3/4] rounded-lg" />
                            ))}
                        </div>
                    ) : images.length === 0 ? (
                        <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                            <p className="font-Mulish font-semibold text-secondary">Nothing here yet</p>
                            <p className="mt-1 font-Mulish text-sm text-graphite">Upload a photo to use it in this look.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 content-start gap-3 sm:grid-cols-3">
                            {images.map((image) => {
                                const selected = (many ? draft : selectedIds).some((item) => item.id === image.id);
                                const name = many ? pieceName(image) : "Choose this person";
                                return (
                                    <button
                                        key={image.id}
                                        type="button"
                                        aria-label={name}
                                        onClick={() => choose(image)}
                                        className={`overflow-hidden rounded-lg border-2 bg-placeholder text-left ${selected ? "border-primary-500" : "border-transparent shadow-[0_0_0_1px_var(--hairline)] hover:border-primary-200"}`}
                                    >
                                        <Photo src={mediaUrl(image.image_url)} className="pointer-events-none aspect-[3/4] w-full" />
                                        {many ? (
                                            <span className="block truncate bg-white px-2 py-1.5 font-Mulish text-xs font-semibold text-secondary">{name}</span>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    {onUpload ? (
                        <button type="button" onClick={onUpload} className="w-full rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50">
                            Upload
                        </button>
                    ) : null}
                    {many ? (
                        <button
                            type="button"
                            disabled={draft.length === 0}
                            onClick={() => onConfirm(draft)}
                            className={`w-full rounded-full px-4 py-2.5 font-Mulish text-sm font-semibold ${draft.length ? "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]" : "bg-primary-100 text-primary-800"}`}
                        >
                            Use {draft.length} {draft.length === 1 ? "piece" : "pieces"}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
